import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { DATA_KEY_ATTR, DATA_KEY_NODE_NAME } from "./constants";
import { DataKeyNodeView } from "./data-key-node";

export interface DataKeyNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    dataKey: {
      /** Insert a dynamic data-key node that resolves the given key. */
      insertDataKey: (dataKey: string) => ReturnType;
    };
  }
}

/**
 * Inline, atomic node representing a bare `{{ data-key }}` reference. Stateless:
 * it stores only the key id and resolves its value live via the dynamic node
 * context. Read-only in the editor.
 */
export const DataKeyNode = Node.create<DataKeyNodeOptions>({
  name: DATA_KEY_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      [DATA_KEY_ATTR]: {
        default: "",
        parseHTML: (element) => element.getAttribute(`data-${DATA_KEY_ATTR}`) ?? "",
        renderHTML: (attributes) => ({
          [`data-${DATA_KEY_ATTR}`]: attributes[DATA_KEY_ATTR],
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${DATA_KEY_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": DATA_KEY_NODE_NAME },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DataKeyNodeView);
  },

  addCommands() {
    return {
      insertDataKey:
        (dataKey: string) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { [DATA_KEY_ATTR]: dataKey },
          }),
    };
  },
});

export default DataKeyNode;
